import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearError } from '../redux/slices/authSlice';
import alertify from 'alertifyjs';

/**
 * useAuthPageGuard - All-in-one auth page protection
 * 
 * Handles:
 * - Redirect authenticated users away from auth pages
 * - Clear errors on mount (optional)
 * - Auto-show error notifications (optional)
 * 
 * Use on: LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage
 * 
 * DRY Principle: Eliminates duplicate useEffect logic across auth pages
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.redirectTo - Where to redirect authenticated users (default: '/dashboard')
 * @param {boolean} options.showErrors - Auto-show error alertify (default: true)
 * @param {boolean} options.clearOnMount - Clear errors on mount (default: true)
 * 
 * @example
 * // Basic usage (LoginPage)
 * useAuthPageGuard();
 * 
 * @example
 * // Custom redirect (RegisterPage with success message)
 * useAuthPageGuard({ redirectTo: '/dashboard' });
 * 
 * @example
 * // Don't clear errors on mount (if using inline form errors)
 * useAuthPageGuard({ clearOnMount: false });
 */
export const useAuthPageGuard = ({
  redirectTo = '/dashboard',
  showErrors = true,
  clearOnMount = true
} = {}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isAuthenticated, error } = useSelector((state) => state.auth);

  // Redirect authenticated users (prevent access to login/register pages)
  useEffect(() => {
    if (isAuthenticated) {
      history.push(redirectTo);
    }
  }, [isAuthenticated, history, redirectTo]);

  // Clear errors on mount (cleanup from previous auth attempts)
  useEffect(() => {
    if (clearOnMount) {
      dispatch(clearError());
    }
  }, [dispatch, clearOnMount]);

  // Show error notifications (auto-display Redux auth errors)
  useEffect(() => {
    if (showErrors && error) {
      alertify.error(error.message || error);
    }
  }, [error, showErrors]);
};

export default useAuthPageGuard;

